import { metricGoals } from './config';
import { KPIMetric, KPISection, KPIUnit } from './types';

export type KPIOutcome = 'GOOD' | 'BAD' | 'NEUTRAL';
export type KPISectionStatus =
  'Increasing' | 'Decreasing' | 'Mixed' | 'Unchanged' | 'No data';

export function orderKPISections(
  sections: KPISection[],
  sectionOrder: string[],
): KPISection[] {
  const sectionsByKey = new Map(
    sections.map((section) => [section.key, section]),
  );
  const orderedSections = sectionOrder
    .map((sectionKey) => sectionsByKey.get(sectionKey))
    .filter((section): section is KPISection => Boolean(section));
  const orderedKeys = new Set(orderedSections.map((section) => section.key));

  return [
    ...orderedSections,
    ...sections.filter((section) => !orderedKeys.has(section.key)),
  ];
}

export function formatKPIValue(value: number | null, unit: KPIUnit): string {
  if (value === null) return 'Not available';

  const formattedValue = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: unit === 'count' ? 0 : 1,
  }).format(value);

  if (unit === 'percent') return `${formattedValue}%`;
  if (unit === 'days') return `${formattedValue} days`;
  return formattedValue;
}

export function formatKPIChartValue(value: number, unit: KPIUnit): string {
  const formattedValue = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: unit === 'count' ? 0 : 1,
  }).format(value);

  if (unit === 'percent') return `${formattedValue}%`;
  return `${formattedValue} ${unit === 'days' ? 'Days' : 'Count'}`;
}

export function getKPIErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return fallback;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return fallback;

  const messages = Object.values(data).flatMap((value) =>
    Array.isArray(value) ? value.map(String) : [String(value)],
  );
  return messages.length > 0 ? messages.join(' ') : fallback;
}

export function isMetricAvailable(metric: KPIMetric): boolean {
  return metric.data_status === 'AVAILABLE' && metric.value !== null;
}

export function getMetricOutcome(metric: KPIMetric): KPIOutcome {
  if (
    !isMetricAvailable(metric) ||
    metric.trend === 'NOT_AVAILABLE' ||
    metric.trend === 'SAME'
  ) {
    return 'NEUTRAL';
  }

  const goal = metric.goal || metricGoals[metric.key];
  if (!goal) return 'NEUTRAL';

  const increased = metric.trend === 'UP';
  const improved =
    (goal === 'higher' && increased) || (goal === 'lower' && !increased);
  return improved ? 'GOOD' : 'BAD';
}

export function getSectionStatus(metrics: KPIMetric[]): KPISectionStatus {
  if (metrics.every((metric) => !isMetricAvailable(metric))) return 'No data';

  const outcomes = metrics.map(getMetricOutcome);
  const goodCount = outcomes.filter((outcome) => outcome === 'GOOD').length;
  const badCount = outcomes.filter((outcome) => outcome === 'BAD').length;

  if (goodCount > 0 && badCount > 0) return 'Mixed';
  if (goodCount > 0) return 'Increasing';
  if (badCount > 0) return 'Decreasing';
  return 'Unchanged';
}
